import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import {
    Container,
    Grid,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    CardActions,
    Divider,
    Chip,
    Tooltip,
    IconButton,
    Fade
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PlanFormModal from "../components/PlanFormModal";
import ContractRequestPlanModal from "../components/ContractRequestPlanModal";
import BaseContractModal from "../components/BaseContractModal";
import { getPlans, createPlan, updatePlan, deletePlan } from "../api/planApi";
import {
    createContractRequestPlan,
    deleteContractRequestPlan,
    getContractRequestPlans,
    updateContractRequestPlan
} from "../api/contractRequestApi";
import {
    createBaseContract,
    deleteBaseContract,
    getBaseContracts,
    updateBaseContract
} from "../api/baseContractApi";
import { getAffiliate } from "../api/affiliateApi";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PersonIcon from '@mui/icons-material/Person';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
    ArrowBackIos,
    ArrowForwardIos,
} from '@mui/icons-material';
import AppTheme from "../shared-theme/AppTheme";
import { formatRegistrationScheduleForDisplay } from "../utils/planSchedule";
import { formatCurrency } from "../utils/currency";

const StyledContainer = styled(Container)(({ theme }) => ({
    pt: { xs: 4, sm: 12 },
    pb: { xs: 8, sm: 16 },
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(4),
    backgroundColor: theme.palette.background.default,
}));

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    textAlign: "center",
    transition: "0.3s",
    backgroundColor: theme.palette.background.paper,
    height: "100%",
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[2],
    "&:hover": {
        boxShadow: theme.shadows[5],
        transform: "translateY(-4px)",
        cursor: "pointer",
    },
}));

const CarouselContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
    boxShadow: theme.shadows[1],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '500px',
}));

const CarouselContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
        maxWidth: '400px',
        margin: '0 auto',
    },
    [theme.breakpoints.up('md')]: {
        maxWidth: '1200px',
        gap: theme.spacing(3),
        padding: theme.spacing(0, 2),
    },
}));

const PlanCard = styled(Box)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '320px',
        margin: '0 auto',
    },
    [theme.breakpoints.up('md')]: {
        width: '100%',
        maxWidth: '350px',
        transition: 'all 0.3s ease',
        '&.side-plan': {
            transform: 'scale(0.85)',
            opacity: 0.7,
            filter: 'grayscale(20%)',
        },
        '&.center-plan': {
            transform: 'scale(1)',
            opacity: 1,
            filter: 'none',
            zIndex: 2,
        },
    },
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[3],
    zIndex: 10,
    width: 48,
    height: 48,
    '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        transform: 'translateY(-50%) scale(1.1)',
    },
    transition: 'all 0.2s ease',
    [theme.breakpoints.down('sm')]: {
        width: 40,
        height: 40,
    },
}));

const PromoTag = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#FF6B35',
    color: 'white',
    padding: theme.spacing(0.5, 2),
    fontSize: '0.75rem',
    fontWeight: 'bold',
    transform: 'rotate(45deg) translate(25%, -50%)',
    transformOrigin: 'center',
    zIndex: 3,
    minWidth: '80px',
    textAlign: 'center',
    boxShadow: theme.shadows[2],
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        height: '4px',
        backgroundColor: '#E55A2B',
    }
}));

const trainingTypeColors = {
    "All classes": "success",
    "WOD": "primary",
    "Weightlifting": "secondary",
    "Cardio": "error",
    "Rowing": "info",
    "Gymnastics": "warning",
    "Open Gym": "default"
};

const TrainingTypeChip = styled(Chip)(({ theme }) => ({
    margin: theme.spacing(0.5),
    fontWeight: 500,
}));

const StatusDot = styled(FiberManualRecordIcon)(({ active, theme }) => ({
    fontSize: '0.75rem',
    color: active ? '#4caf50' : '#f44336',
    marginRight: theme.spacing(0.5),
}));

const ensureTrainingTypeArray = (trainingType) => {
    if (!trainingType) return [];
    if (Array.isArray(trainingType)) return trainingType;
    if (typeof trainingType === 'string') {
        try {
            const parsed = JSON.parse(trainingType);
            return Array.isArray(parsed) ? parsed : [trainingType];
        } catch (e) {
            return [trainingType];
        }
    }
    return [trainingType];
};

export default function Plans() {
    const theme = useTheme();
    const [plans, setPlans] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [galleryGroups, setGalleryGroups] = useState([]);
    const [standalonePlans, setStandalonePlans] = useState([]);
    const [carouselIndices, setCarouselIndices] = useState({});
    const [paymentCurrency, setPaymentCurrency] = useState("EUR");
    const [contractPlans, setContractPlans] = useState([]);
    const [contractModalOpen, setContractModalOpen] = useState(false);
    const [selectedContractPlan, setSelectedContractPlan] = useState(null);
    const [baseContracts, setBaseContracts] = useState([]);
    const [baseContractModalOpen, setBaseContractModalOpen] = useState(false);
    const [selectedBaseContract, setSelectedBaseContract] = useState(null);
    const [affiliateId, setAffiliateId] = useState(null);
    const [minimumContractValidityMonths, setMinimumContractValidityMonths] = useState(2);

    useEffect(() => {
        fetchPlans();
        fetchAffiliateCurrency();
        fetchContractPlans();
        fetchBaseContracts();
    }, []);

    useEffect(() => {
        organizePlans();
    }, [plans]);

    const fetchPlans = async () => {
        try {
            const data = await getPlans();
            if (Array.isArray(data)) {
                const processedPlans = data.map(plan => ({
                    ...plan,
                    trainingType: ensureTrainingTypeArray(plan.trainingType)
                }));
                setPlans(processedPlans);
            } else {
                setPlans([]);
            }
        } catch (error) {
            console.error("Error loading plans:", error);
            setPlans([]);
        }
    };

    const fetchAffiliateCurrency = async () => {
        try {
            const data = await getAffiliate();
            setPaymentCurrency(data?.affiliate?.paymentCurrency || "EUR");
            setAffiliateId(data?.affiliate?.id || null);
            setMinimumContractValidityMonths(data?.affiliate?.minimumContractValidityMonths ?? 2);
        } catch (error) {
            console.error("Error loading affiliate currency:", error);
            setPaymentCurrency("EUR");
        }
    };

    const fetchBaseContracts = async () => {
        try {
            const data = await getBaseContracts();
            setBaseContracts(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error loading base contracts:", error);
            setBaseContracts([]);
        }
    };

    const fetchContractPlans = async () => {
        try {
            const data = await getContractRequestPlans();
            setContractPlans(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error loading contract request plans:", error);
            setContractPlans([]);
        }
    };

    const organizePlans = () => {
        const groups = [];
        const standalone = [];
        const processedPlanIds = new Set();
        const indices = {};

        // Leia galerii pealplaanid - need, millel on galleryPlans array täielike objektidega
        const galleryMainPlans = plans.filter(plan => {
            return plan.galleryPlans && Array.isArray(plan.galleryPlans) && plan.galleryPlans.length > 0;
        });

        // Töötle galerii pealplaanid
        galleryMainPlans.forEach(plan => {
            if (processedPlanIds.has(plan.id)) return;

            // Galerii plaanid on juba täielikud objektid
            const validGalleryPlans = plan.galleryPlans.filter(p =>
                !processedPlanIds.has(p.id) && p.active
            );

            if (validGalleryPlans.length > 0) {
                const groupId = `gallery-${plan.id}`;
                const group = {
                    id: groupId,
                    mainPlan: plan,
                    plans: [plan, ...validGalleryPlans],
                    isGallery: true
                };

                groups.push(group);
                indices[groupId] = 0;
                processedPlanIds.add(plan.id);
                validGalleryPlans.forEach(p => processedPlanIds.add(p.id));
            } else {
                standalone.push(plan);
                processedPlanIds.add(plan.id);
            }
        });

        // Seejärel töötle ülejäänud plaanid
        plans.forEach(plan => {
            if (processedPlanIds.has(plan.id)) return;
            standalone.push(plan);
            processedPlanIds.add(plan.id);
        });

        // Sorteeri standalone plaanid - special price plaanid esimesena, seejärel hinna järgi
        const sortedStandalone = standalone.sort((a, b) => {
            // Kui üks on special price ja teine mitte
            if (a.isSpecialPrice && !b.isSpecialPrice) return -1;
            if (!a.isSpecialPrice && b.isSpecialPrice) return 1;

            // Kui mõlemad on special price või kumbki pole, sorteeri hinna järgi
            return parseFloat(a.price || 0) - parseFloat(b.price || 0);
        });

        // Sorteeri gallery grupid - need, mis sisaldavad special price plaane, tulevad esimesena
        const sortedGalleryGroups = groups.sort((a, b) => {
            const aHasSpecialPrice = a.plans.some(plan => plan.isSpecialPrice);
            const bHasSpecialPrice = b.plans.some(plan => plan.isSpecialPrice);

            // Kui üks sisaldab special price ja teine mitte
            if (aHasSpecialPrice && !bHasSpecialPrice) return -1;
            if (!aHasSpecialPrice && bHasSpecialPrice) return 1;

            // Kui mõlemad sisaldavad või ei sisalda special price, jäävad samasse järjekorda
            return 0;
        });

        // Jaota standalone plaanid special price ja tavalisteks
        const specialPriceStandalone = sortedStandalone.filter(plan => plan.isSpecialPrice);
        const regularStandalone = sortedStandalone.filter(plan => !plan.isSpecialPrice);

        setGalleryGroups(sortedGalleryGroups);
        setStandalonePlans({ specialPrice: specialPriceStandalone, regular: regularStandalone });
        setCarouselIndices(indices);
    };

    const handleSave = async (planData) => {
        try {
            if (!planData) {
                await deletePlan(selectedPlan.id);
            } else if (selectedPlan) {
                await updatePlan(selectedPlan.id, planData);
            } else {
                await createPlan(planData);
            }
            setModalOpen(false);
            setSelectedPlan(null);
            fetchPlans();
        } catch (error) {
            console.error("Error saving plan:", error);
        }
    };

    const handleSaveContractPlan = async (planData) => {
        if (selectedContractPlan) {
            await updateContractRequestPlan(selectedContractPlan.id, planData);
        } else {
            await createContractRequestPlan(planData);
        }
        setContractModalOpen(false);
        setSelectedContractPlan(null);
        await fetchContractPlans();
    };

    const handleDeleteContractPlan = async (id) => {
        await deleteContractRequestPlan(id);
        setContractModalOpen(false);
        setSelectedContractPlan(null);
        await fetchContractPlans();
    };

    const handleSaveBaseContract = async (baseContractData) => {
        if (selectedBaseContract) {
            await updateBaseContract(selectedBaseContract.id, baseContractData);
        } else {
            await createBaseContract(baseContractData);
        }
        setBaseContractModalOpen(false);
        setSelectedBaseContract(null);
        await fetchBaseContracts();
    };

    const handleDeleteBaseContract = async (id) => {
        await deleteBaseContract(id);
        setBaseContractModalOpen(false);
        setSelectedBaseContract(null);
        await fetchBaseContracts();
    };

    const getPlanDescription = (plan) => {
        if (plan.sessions === 9999) {
            return "Unlimited sessions";
        } else {
            return `${plan.sessions} sessions`;
        }
    };

    const getPlanValidityText = (plan) => {
        if (plan.singleCalendarDayValidity) {
            return "1 calendar day";
        }

        if (plan.exactMonthValidity) {
            return "1 month";
        }
        return `${plan.validityDays} days`;
    };

    const handleSelectPlan = (plan) => {
        const updatedPlan = {
            ...plan,
            trainingType: ensureTrainingTypeArray(plan.trainingType)
        };
        setSelectedPlan(updatedPlan);
        setModalOpen(true);
    };

    const updateCarouselIndex = (groupId, newIndex) => {
        setCarouselIndices(prev => ({
            ...prev,
            [groupId]: newIndex
        }));
    };

    const renderPlanCard = (plan) => {
        const isPremium = plan.name === "Professional" || plan.name.toLowerCase().includes("premium");
        const isSpecialPrice = plan.isSpecialPrice;
        const scheduleLines = formatRegistrationScheduleForDisplay(
            plan.hasRegistrationSchedule,
            plan.registrationSchedule
        );

        return (
            <StyledCard
                onClick={() => handleSelectPlan(plan)}
                sx={isPremium ? {
                    border: "none",
                    boxShadow: "0 8px 12px hsla(220, 20%, 42%, 0.2)",
                    bgcolor: "background.paper",
                    borderLeft: "5px solid #FFB347",
                    position: "relative",
                    overflow: "hidden"
                } : {
                    position: "relative",
                    overflow: "hidden"
                }}
            >
                {/* Special Price Promo Tag */}
                {isSpecialPrice && (
                    <PromoTag>
                        PROMO
                    </PromoTag>
                )}

                <Box
                    sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    <StatusDot active={plan.active} />
                    <Typography
                        variant="caption"
                        color={plan.active ? "success.main" : "error.main"}
                    >
                        {plan.active ? "Active" : "Inactive"}
                    </Typography>
                </Box>

                <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", mt: 2 }}>
                    <Box sx={{ mb: 1, display: "flex", alignItems: "baseline", justifyContent: "center", gap: 2 }}>
                        <Typography component="h3" variant="h5" fontWeight="bold">
                            Plan ID: {plan.id}
                        </Typography>
                    </Box>
                    <Box sx={{ mb: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 2, position: "relative" }}>
                        <Typography component="h3" variant="h5" fontWeight="bold" textAlign="center">
                            {plan.name}
                        </Typography>
                        {isPremium && (
                            <AutoAwesomeIcon
                                color="warning"
                                sx={{
                                    position: "absolute",
                                    right: 0,
                                    top: "50%",
                                    transform: "translateY(-50%)"
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", mb: 2 }}>
                        <Typography component="h3" variant="h2" fontWeight="bold">
                            {formatCurrency(plan.price, paymentCurrency)}
                        </Typography>
                        {isSpecialPrice && (
                            <Chip
                                icon={<LocalOfferIcon />}
                                label="Special Price"
                                color="error"
                                variant="outlined"
                                size="small"
                                sx={{ ml: 1 }}
                            />
                        )}
                    </Box>

                    <Divider sx={{ my: 2, opacity: 0.8, borderColor: "divider" }} />

                    <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
                        <CheckCircleRoundedIcon color="primary" />
                        <Typography variant="body1">{getPlanValidityText(plan)}</Typography>
                    </Box>

                    <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
                        <CheckCircleRoundedIcon color="primary" />
                        <Typography variant="body1">{getPlanDescription(plan)}</Typography>
                    </Box>

                    {scheduleLines.length > 0 && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                                Registration schedule:
                            </Typography>
                            {scheduleLines.map((line) => (
                                <Typography key={line} variant="body2" color="text.primary">
                                    {line}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                        {plan.firstBuyOnly && (
                            <Typography variant="body1" fontWeight="bold" color="primary">
                                First purchase only
                            </Typography>
                        )}
                        {plan.oneTimeOffer && (
                            <Chip
                                icon={<LocalOfferIcon />}
                                label="One Time Offer"
                                color="warning"
                                variant="filled"
                                size="small"
                            />
                        )}
                    </Box>

                    <Box sx={{ mt: 2, mb: 3 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: "medium" }}>
                            <FitnessCenterIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
                            Included Training Types:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", minHeight: "70px" }}>
                            {plan.trainingType && plan.trainingType.length > 0 ? (
                                ensureTrainingTypeArray(plan.trainingType).map((type, index) => (
                                    <Tooltip title={type} key={index}>
                                        <TrainingTypeChip
                                            label={type}
                                            color={trainingTypeColors[type] || "default"}
                                            size="small"
                                        />
                                    </Tooltip>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No training types specified
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {plan.additionalData && (
                        <Box sx={{ mt: "auto", mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {plan.additionalData}
                            </Typography>
                        </Box>
                    )}

                    {plan.paymentDescription && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
                                Payment Description:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                {plan.paymentDescription}
                            </Typography>
                        </Box>
                    )}

                    {plan.assignTo === 1 && (
                        <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                            <PersonIcon color="primary" fontSize="small" />
                            <Typography variant="body2" color="primary" fontWeight="700">
                                Assigned to Users
                            </Typography>
                        </Box>
                    )}

                    {/* Gallery indicator */}
                    {plan.galleryPlans && Array.isArray(plan.galleryPlans) && plan.galleryPlans.length > 0 && (
                        <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                            <AutoAwesomeIcon color="secondary" fontSize="small" />
                            <Typography variant="body2" color="secondary" fontWeight="700">
                                Gallery Main Plan ({plan.galleryPlans.length} plans)
                            </Typography>
                        </Box>
                    )}
                </CardContent>
                <CardActions>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        sx={{
                            borderRadius: "20px",
                            "&:hover": {
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText
                            }
                        }}
                    >
                        Edit Plan
                    </Button>
                </CardActions>
            </StyledCard>
        );
    };

    const renderCarousel = (group) => {
        const currentIndex = carouselIndices[group.id] || 0;
        const totalPlans = group.plans.length;

        const nextSlide = () => {
            const newIndex = (currentIndex + 1) % totalPlans;
            updateCarouselIndex(group.id, newIndex);
        };

        const prevSlide = () => {
            const newIndex = (currentIndex - 1 + totalPlans) % totalPlans;
            updateCarouselIndex(group.id, newIndex);
        };

        const setSlideIndex = (index) => {
            updateCarouselIndex(group.id, index);
        };

        const getVisiblePlans = () => {
            const isMobile = window.innerWidth < 900;

            if (isMobile) {
                return [{
                    plan: group.plans[currentIndex],
                    position: 'center'
                }];
            } else {
                const visiblePlans = [];

                if (totalPlans === 1) {
                    visiblePlans.push({
                        plan: group.plans[0],
                        position: 'center'
                    });
                } else if (totalPlans === 2) {
                    visiblePlans.push({
                        plan: group.plans[currentIndex],
                        position: 'center'
                    });
                    visiblePlans.push({
                        plan: group.plans[(currentIndex + 1) % totalPlans],
                        position: 'right'
                    });
                } else {
                    const leftIndex = (currentIndex - 1 + totalPlans) % totalPlans;
                    const rightIndex = (currentIndex + 1) % totalPlans;

                    visiblePlans.push({
                        plan: group.plans[leftIndex],
                        position: 'left'
                    });
                    visiblePlans.push({
                        plan: group.plans[currentIndex],
                        position: 'center'
                    });
                    visiblePlans.push({
                        plan: group.plans[rightIndex],
                        position: 'right'
                    });
                }

                return visiblePlans;
            }
        };

        const visiblePlans = getVisiblePlans();
        const isMobile = window.innerWidth < 900;

        return (
            <Box key={group.id} sx={{ mb: 6 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                        Plan Gallery
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {totalPlans} plans in this collection
                    </Typography>
                </Box>

                <CarouselContainer>
                    <CarouselContent>
                        {totalPlans > 1 && (
                            <NavigationButton
                                onClick={prevSlide}
                                sx={{
                                    left: { xs: 10, sm: 20 },
                                    zIndex: 10
                                }}
                            >
                                <ArrowBackIos />
                            </NavigationButton>
                        )}

                        {isMobile ? (
                            <PlanCard>
                                <Fade in={true} key={visiblePlans[0].plan.id}>
                                    <Box>
                                        {renderPlanCard(visiblePlans[0].plan)}
                                    </Box>
                                </Fade>
                            </PlanCard>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 3,
                                width: '100%'
                            }}>
                                {visiblePlans.map((item, index) => (
                                    <PlanCard
                                        key={`${item.plan.id}-${index}`}
                                        className={item.position === 'center' ? 'center-plan' : 'side-plan'}
                                    >
                                        {renderPlanCard(item.plan)}
                                    </PlanCard>
                                ))}
                            </Box>
                        )}

                        {totalPlans > 1 && (
                            <NavigationButton
                                onClick={nextSlide}
                                sx={{
                                    right: { xs: 10, sm: 20 },
                                    zIndex: 10
                                }}
                            >
                                <ArrowForwardIos />
                            </NavigationButton>
                        )}
                    </CarouselContent>

                    {totalPlans > 1 && (
                        <Box sx={{
                            position: 'absolute',
                            bottom: { xs: 8, md: 12 },
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            gap: 1
                        }}>
                            {group.plans.map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: index === currentIndex ? 'primary.main' : 'grey.300',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: 'primary.main',
                                            transform: 'scale(1.2)'
                                        }
                                    }}
                                    onClick={() => setSlideIndex(index)}
                                />
                            ))}
                        </Box>
                    )}
                </CarouselContainer>
            </Box>
        );
    };

    return (
        <AppTheme>
            <StyledContainer maxWidth={false}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                    <Typography
                        component="h2"
                        variant="h4"
                        sx={{
                            color: "text.primary",
                            fontWeight: "bold",
                            mb: 2
                        }}
                    >
                        Training Plans
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.secondary",
                            maxWidth: "700px",
                            mx: "auto"
                        }}
                    >
                        Manage your training plans. Create galleries by adding plans to collections.
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1.5, width: "100%", mb: 2 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            setSelectedBaseContract(null);
                            setBaseContractModalOpen(true);
                        }}
                        sx={{ borderRadius: "20px" }}
                    >
                        Add Base Contract
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            setSelectedContractPlan(null);
                            setContractModalOpen(true);
                        }}
                        sx={{ borderRadius: "20px" }}
                    >
                        Add Contract Request
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedPlan(null);
                            setModalOpen(true);
                        }}
                        startIcon={<AutoAwesomeIcon />}
                        sx={{ borderRadius: "20px" }}
                    >
                        Add New Plan
                    </Button>
                </Box>

                <Box sx={{ width: "100%" }}>
                    <Fade in={true}>
                        <Box>
                            {/* Render Special Price Standalone Plans First */}
                            {standalonePlans.specialPrice && standalonePlans.specialPrice.length > 0 && (
                                <Box sx={{ mb: 4 }}>
                                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                                        <Typography variant="h6" color="error.main" gutterBottom>
                                            🔥 Special Offers
                                        </Typography>
                                        <Divider sx={{ maxWidth: 200, mx: 'auto', borderColor: 'error.main' }} />
                                    </Box>

                                    <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
                                        {standalonePlans.specialPrice.map((plan) => (
                                            <Grid item xs={12} sm={6} md={4} key={plan.id}>
                                                {renderPlanCard(plan)}
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {/* Render Gallery Groups */}
                            {galleryGroups.map((group) => renderCarousel(group))}

                            {/* Render Regular Standalone Plans */}
                            {standalonePlans.regular && standalonePlans.regular.length > 0 && (
                                <Box sx={{ mt: (galleryGroups.length > 0 || (standalonePlans.specialPrice && standalonePlans.specialPrice.length > 0)) ? 4 : 0 }}>
                                    {(galleryGroups.length > 0 || (standalonePlans.specialPrice && standalonePlans.specialPrice.length > 0)) && (
                                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                                            <Typography variant="h6" color="text.primary" gutterBottom>
                                                Individual Plans
                                            </Typography>
                                            <Divider sx={{ maxWidth: 200, mx: 'auto' }} />
                                        </Box>
                                    )}

                                    <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
                                        {standalonePlans.regular.map((plan) => (
                                            <Grid item xs={12} sm={6} md={4} key={plan.id}>
                                                {renderPlanCard(plan)}
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {baseContracts.length > 0 && (
                                <Box sx={{ mt: 6 }}>
                                    <Box sx={{ textAlign: "center", mb: 3 }}>
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                                            Base Contracts
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Customers join these on their own and pay immediately. No action needed from you.
                                        </Typography>
                                        <Divider sx={{ maxWidth: 200, mx: "auto", mt: 1.5 }} />
                                    </Box>
                                    <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
                                        {[...baseContracts]
                                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                                            .map((baseContract) => (
                                                <Grid item xs={12} sm={6} md={4} key={baseContract.id}>
                                                    <StyledCard
                                                        onClick={() => {
                                                            setSelectedBaseContract(baseContract);
                                                            setBaseContractModalOpen(true);
                                                        }}
                                                        sx={{ position: "relative", overflow: "hidden" }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: "absolute",
                                                                top: 10,
                                                                left: 10,
                                                                display: "flex",
                                                                alignItems: "center"
                                                            }}
                                                        >
                                                            <StatusDot active={baseContract.active} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {baseContract.active ? "Active" : "Inactive"}
                                                            </Typography>
                                                        </Box>
                                                        <CardContent sx={{ flexGrow: 1 }}>
                                                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                                {baseContract.name}
                                                            </Typography>
                                                            <Typography component="h3" variant="h2" color="primary" fontWeight="bold">
                                                                {formatCurrency(baseContract.monthlyPrice, paymentCurrency)}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                per month
                                                            </Typography>
                                                            {baseContract.additionalInfo && (
                                                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                                                                    {baseContract.additionalInfo}
                                                                </Typography>
                                                            )}
                                                            <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
                                                                <Chip
                                                                    size="small"
                                                                    color="primary"
                                                                    label={`${baseContract.durationYears} ${baseContract.durationYears === 1 ? "year" : "years"}`}
                                                                />
                                                                <Chip
                                                                    size="small"
                                                                    label={baseContract.paymentDayMode === "FIXED"
                                                                        ? `Fixed days: ${(baseContract.fixedPaymentDays || []).join(", ")}`
                                                                        : "Flexible payment day"}
                                                                />
                                                                {baseContract.discountMonths > 0 && (
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        icon={<LocalOfferIcon />}
                                                                        label={`${baseContract.discountPercent}% x ${baseContract.discountMonths}`}
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Box sx={{ mt: 1.5, display: "flex", justifyContent: "center", gap: 0.5, flexWrap: "wrap" }}>
                                                                {(baseContract.trainingTypes || []).map((type) => (
                                                                    <TrainingTypeChip
                                                                        key={type}
                                                                        size="small"
                                                                        label={type}
                                                                        color={trainingTypeColors[type] || "default"}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </CardContent>
                                                        <CardActions>
                                                            <Button fullWidth>Edit</Button>
                                                        </CardActions>
                                                    </StyledCard>
                                                </Grid>
                                            ))}
                                    </Grid>
                                </Box>
                            )}

                            {contractPlans.length > 0 && (
                                <Box sx={{ mt: 6 }}>
                                    <Box sx={{ textAlign: "center", mb: 3 }}>
                                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                                            Contract Requests
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Contract offerings shown to customers.
                                        </Typography>
                                        <Divider sx={{ maxWidth: 200, mx: "auto", mt: 1.5 }} />
                                    </Box>
                                    <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
                                        {[...contractPlans]
                                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                                            .map((contractPlan) => (
                                                <Grid item xs={12} sm={6} md={4} key={contractPlan.id}>
                                                    <StyledCard
                                                        onClick={() => {
                                                            setSelectedContractPlan(contractPlan);
                                                            setContractModalOpen(true);
                                                        }}
                                                    >
                                                        <CardContent sx={{ flexGrow: 1 }}>
                                                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                                {contractPlan.name}
                                                            </Typography>
                                                            <Typography component="h3" variant="h2" color="primary" fontWeight="bold">
                                                                {formatCurrency(contractPlan.monthlyPrice, paymentCurrency)}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                per month
                                                            </Typography>
                                                            {contractPlan.additionalInfo && (
                                                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                                                                    {contractPlan.additionalInfo}
                                                                </Typography>
                                                            )}
                                                            <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
                                                                <Chip
                                                                    size="small"
                                                                    label={contractPlan.paymentDayMode === "FIXED"
                                                                        ? `Fixed days: ${(contractPlan.fixedPaymentDays || []).join(", ")}`
                                                                        : "Flexible payment day"}
                                                                />
                                                                <Chip
                                                                    size="small"
                                                                    variant="outlined"
                                                                    label={`${(contractPlan.questions || []).length} questions`}
                                                                />
                                                            </Box>
                                                        </CardContent>
                                                        <CardActions>
                                                            <Button fullWidth>Edit</Button>
                                                        </CardActions>
                                                    </StyledCard>
                                                </Grid>
                                            ))}
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    </Fade>
                </Box>

                <PlanFormModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSave} plan={selectedPlan} />
                <ContractRequestPlanModal
                    open={contractModalOpen}
                    plan={selectedContractPlan}
                    onClose={() => {
                        setContractModalOpen(false);
                        setSelectedContractPlan(null);
                    }}
                    onSave={handleSaveContractPlan}
                    onDelete={handleDeleteContractPlan}
                />
                <BaseContractModal
                    open={baseContractModalOpen}
                    baseContract={selectedBaseContract}
                    affiliateId={affiliateId}
                    minimumContractValidityMonths={minimumContractValidityMonths}
                    onClose={() => {
                        setBaseContractModalOpen(false);
                        setSelectedBaseContract(null);
                    }}
                    onSave={handleSaveBaseContract}
                    onDelete={handleDeleteBaseContract}
                />
            </StyledContainer>
        </AppTheme>
    );
}